import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { Subcategory, QueryParams, DataGridPagination } from "@/sharedTypes";
import { useCreateSubcategoryMutation, useGetSubcategoriesListQuery } from "@/api/ticketApi/subcategory";
import SubcategoryListTable from "@/pages/subcategories/SubcategoryListTable";
import SubcategoryFormDialog from "@/pages/subcategories/SubcategoryFormDialog";
import SubcategoryFilters from "@/pages/subcategories/SubcategoryFilters";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import LoadingPage from "@/components/LoadingPage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function SubcategoryList() {
  const { t } = useTranslation(["subcategory", "common"]);
  const user = useCurrentUser();
  const [showInactive, setShowInactive] = useState(false);
  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: { is_active: true },
    pagination: { page: 1, pageSize: 15 },
  });
  const [additionalFilters, setAdditionalFilters] = useState<any>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const listParams = useMemo(
    () => ({
      filters: { ...queryParams.filters, ...additionalFilters, is_active: !showInactive },
      pagination: queryParams.pagination,
    }),
    [queryParams.filters, queryParams.pagination, showInactive, additionalFilters]
  );

  const {
    data: subcategoriesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetSubcategoriesListQuery(listParams, { refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (isError) {
      toast.error(getErrorMessage(error));
    }
  }, [isError, error]);

  const [createSubcategory] = useCreateSubcategoryMutation();

  const handleCreate = (data: Subcategory) => {
    createSubcategory(data)
      .unwrap()
      .then(() => {
        toast.success(t("subcategory:created_successfully"));
        setIsCreateModalOpen(false);
        refetch();
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleToggleInactive = (value: boolean) => {
    setShowInactive(value);
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: 1 },
    }));
  };
  
  const handleFilterChange = (filters: any) => {
    setAdditionalFilters(filters);
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page: 1 },
    }));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const pagination: DataGridPagination = useMemo(() => {
    const pagination = subcategoriesData?.pagination || {};
    return {
      pageIndex: pagination.pageIndex ?? pagination.page ?? queryParams.pagination.page,
      pageSize: pagination.pageSize ?? queryParams.pagination.pageSize,
      totalPages: pagination.totalPages ?? 1,
      totalItems: pagination.totalItems ?? pagination.total ?? 0,
    };
  }, [subcategoriesData, queryParams.pagination.page, queryParams.pagination.pageSize]);

  return (
    <NavbarSidebarLayout>
      {isLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("subcategory:subcategories_title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("subcategory:subcategories_title")}
                </h1>
                <p className="text-gray-500">{t("subcategory:manage_subcategories")}</p>
              </div>
              {user?.agent_role === agentRoles.company_admin && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("subcategory:create_subcategory")}
                </Button>
              )}
            </div>

            <SubcategoryFilters
              showInactive={showInactive}
              onToggleInactive={handleToggleInactive}
              onFilterChange={handleFilterChange}
            />

            <SubcategoryListTable
              data={subcategoriesData?.items || []}
              pagination={pagination}
              onPageChange={handlePageChange}
              showInactive={showInactive}
              onRefresh={refetch}
            />
          </div>
        </div>
      </div>}

      <SubcategoryFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
